import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import translations from '../../translations/de';
import Table from '../../components/Table';
import Select from '../../components/Select';
import InputSeason from '../../components/InputSeason/InputSeason';
import parkingAPI from '../../api/parking';
import { formatDate } from '../../helpers/formatDate';

const ParkingStats = ({ store }) => {
    const [fields, setFields] = useState([]);
    const [fieldId, setFieldId] = useState(null);
    const [seasonId, setSeasonId] = useState(null);
    const [perGame, setPerGame] = useState([]);

    useEffect(() => {
        parkingAPI
            .getFields()
            .then((res) => setFields(res.data))
            .catch(() => {});
    }, []);

    useEffect(() => {
        store.getCounts(fieldId, seasonId);
        store.getShowRate(fieldId, seasonId);
        parkingAPI
            .getPerGame({ params: { parking_field_id: fieldId, season_id: seasonId } })
            .then((res) => setPerGame(res.data || []))
            .catch(() => setPerGame([]));
    }, [store, fieldId, seasonId]);

    const fieldName = (id) => fields.find((f) => f.id === id)?.name ?? id;

    const perGameColumns = [
        {
            Header: 'Spiel',
            accessor: 'spiel',
            Cell: ({ value }) => <div style={{ whiteSpace: 'nowrap' }}>{value}</div>,
        },
        {
            Header: translations.parking_field,
            accessor: 'field',
            Cell: ({ value }) => <div>{value}</div>,
        },
        {
            Header: 'Genutzt (swiped)',
            accessor: 'used',
            Cell: ({ value }) => <div>{value}</div>,
        },
        {
            Header: 'Kapazität (physisch)',
            accessor: 'capacity',
            Cell: ({ value }) => <div>{value}</div>,
        },
        {
            Header: 'Status',
            accessor: 'status',
            Cell: ({ row }) =>
                row.original.reached ? (
                    <div style={{ color: '#e73c37', fontWeight: 'bold' }}>Kapazität erreicht</div>
                ) : (
                    <div>
                        {row.original.used}/{row.original.capacity}
                    </div>
                ),
        },
    ];

    const showRateColumns = [
        { Header: 'ID', accessor: 'id', Cell: ({ value }) => <div>{value}</div> },
        {
            Header: translations.parking_field,
            accessor: 'field',
            Cell: ({ value }) => <div>{value}</div>,
        },
        { Header: translations.type, accessor: 'type', Cell: ({ value }) => <div>{value}</div> },
        { Header: 'Kontakt', accessor: 'user', Cell: ({ value }) => <div>{value}</div> },
        { Header: 'Spiele', accessor: 'games', Cell: ({ value }) => <div>{value}</div> },
        {
            Header: 'Show-Rate',
            accessor: 'showrate',
            Cell: ({ value }) => <div>{value === null ? '-' : `${value}%`}</div>,
        },
    ];

    return (
        <div style={{ width: '100%' }}>
            <div className="tab-controls">
                <Select
                    label={translations.parking_field}
                    defaultValue={{ label: translations.all, value: null }}
                    options={[
                        { label: translations.all, value: null },
                        ...fields.map((f) => ({ label: f.name, value: f.id })),
                    ]}
                    placeholder={translations.parking_field}
                    onChange={(e) => setFieldId(e.value)}
                />
                <InputSeason selectedSeason={seasonId} setSelectedSeason={setSeasonId} />
            </div>

            <div
                style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', margin: '20px 2.5%' }}
            >
                {store.counts.map((c) => (
                    <div
                        key={c.parking_field_id}
                        style={{
                            background: '#fff',
                            borderRadius: '8px',
                            padding: '15px 20px',
                            minWidth: '200px',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                        }}
                    >
                        <h4 style={{ margin: '0 0 10px', color: '#333' }}>
                            {fieldName(c.parking_field_id)}
                        </h4>
                        <div style={{ color: '#333' }}>
                            Kapazität (physisch): <b>{c.capacity}</b>
                        </div>
                        <div style={{ color: '#333' }}>
                            Erstellt: <b>{c.issued}</b>
                            {c.issued > c.capacity && (
                                <span style={{ color: '#e08600' }}> (Überbuchung)</span>
                            )}
                        </div>
                        <div style={{ color: '#333' }}>
                            Zugeteilt: <b>{c.assigned}</b>
                        </div>
                        <div style={{ color: '#333' }}>
                            Aktiviert: <b>{c.activated}</b>
                        </div>
                        <div style={{ color: '#333' }}>
                            Nicht aktiviert: <b>{c.not_activated}</b>
                        </div>
                        <div style={{ color: '#333' }}>
                            Verfügbar: <b>{c.available}</b>
                        </div>
                        <div style={{ color: '#333' }}>
                            Genutzt: <b>{c.used}</b>
                        </div>
                    </div>
                ))}
            </div>

            <Table
                tableName="Pro Spiel (swiped / Kapazität)"
                columns={perGameColumns}
                data={perGame.map((r) => ({
                    spiel: `${r.game_date ? formatDate(r.game_date) : ''}${
                        r.opponent ? ` · ${r.opponent}` : ''
                    }`,
                    field: r.field_name,
                    used: r.used,
                    capacity: r.capacity,
                    reached: r.capacity_reached,
                }))}
            />

            <Table
                tableName="Show-Rate"
                columns={showRateColumns}
                data={store.showRateRows.map((r) => ({
                    id: r.parking_pass_id,
                    field: r.parking_field ?? '-',
                    type: r.type === 'single_game' ? translations.single_game : translations.full_season,
                    user: r.user
                        ? [r.user.first_name, r.user.last_name].filter(Boolean).join(' ') ||
                          r.user.email ||
                          `#${r.user.id}`
                        : '-',
                    games: `${r.games_used}/${r.games_total}`,
                    showrate: r.showrate,
                }))}
            />
        </div>
    );
};

export default observer(ParkingStats);
